import type {
  CustomerBrief,
  DeliverabilityReason,
  StopBrief,
  StopLocationType,
  UserBrief,
  VehicleBrief,
} from '#/shared/api/generated/shipment/models'

/**
 * Inline SMS deliverability riding on the list item's customer — a structural
 * match for `CustomerDeliverability` in `entities/number-health`, kept as its
 * own shape here so entity slices stay import-independent of each other.
 */
export interface CustomerDeliverability {
  sms_deliverable?: boolean
  deliverability_reason?: DeliverabilityReason | null
}

export function formatStop(stop: StopBrief): string {
  const cityState = [stop.city ?? '', stop.state ?? '']
    .filter(Boolean)
    .join(', ')
  return [cityState, stop.zip ?? ''].filter(Boolean).join(' ')
}

export function formatVehicleName(vehicle: VehicleBrief): string {
  return `${vehicle.year} ${vehicle.make} ${vehicle.model}`.trim()
}

export function formatOwnerName(
  user: UserBrief | null | undefined,
): string | undefined {
  if (!user) return undefined
  const name = [user.first_name, user.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return name.length > 0 ? name : undefined
}

export function formatCustomer(customer: CustomerBrief | null | undefined): {
  id: string
  name: string
  phone: string
  email: string
  deliverability: CustomerDeliverability | undefined
} {
  if (!customer) {
    return { id: '', name: '', phone: '', email: '', deliverability: undefined }
  }
  const name = [customer.first_name, customer.last_name]
    .filter(Boolean)
    .join(' ')
    .trim()
  return {
    id: customer.id,
    name,
    phone: customer.phone ?? '',
    email: customer.email ?? '',
    deliverability: {
      sms_deliverable: customer.sms_deliverable,
      deliverability_reason: customer.deliverability_reason,
    },
  }
}

export function describeVehicles(vehicles: VehicleBrief[] | undefined): {
  name: string
  extraCount: number
  isInoperable?: boolean
} {
  const list = vehicles ?? []
  if (list.length === 0) return { name: '', extraCount: 0 }
  const first = list[0]
  const someInoperable = list.some((v) => v.is_inoperable === true)
  const everyOperable = list.every((v) => v.is_inoperable === false)
  const isInoperable = someInoperable ? true : everyOperable ? false : undefined
  return {
    name: formatVehicleName(first),
    extraCount: list.length - 1,
    isInoperable,
  }
}

export const STOP_TYPE_PICKUP = 'pickup'
export const STOP_TYPE_DELIVERY = 'delivery'

export function pickRouteEndpoints<T extends { type: string }>(
  stops: T[] | undefined,
): { origin: T | null; destination: T | null } {
  const list = stops ?? []
  const origin = list.find((s) => s.type === STOP_TYPE_PICKUP) ?? null
  const deliveries = list.filter((s) => s.type === STOP_TYPE_DELIVERY)
  const destination =
    deliveries.length > 0 ? deliveries[deliveries.length - 1] : null
  return { origin, destination }
}

export function stopLocationTypeKey(
  stop: { location_type?: StopLocationType | null } | null | undefined,
): string | undefined {
  const value = stop?.location_type
  if (!value) return undefined
  return `orderCreate.location.typeOptions.${value}`
}

export function stopLocationTypeAbbrKey(
  stop: { location_type?: StopLocationType | null } | null | undefined,
): string | undefined {
  const value = stop?.location_type
  if (!value) return undefined
  return `orderCreate.location.typeAbbr.${value}`
}

export function describeRoute(stops: StopBrief[] | undefined): {
  origin: string
  destination: string
} {
  const { origin, destination } = pickRouteEndpoints(stops)
  return {
    origin: origin ? formatStop(origin) : '',
    destination: destination ? formatStop(destination) : '',
  }
}

export function parseTariff(
  raw: number | string | null | undefined,
): number | undefined {
  if (raw == null) return undefined
  const n = typeof raw === 'number' ? raw : Number.parseFloat(raw)
  return Number.isFinite(n) ? n : undefined
}

export function formatShipmentStatus(raw: string | undefined): string {
  if (!raw) return ''
  return raw
    .split('_')
    .map((part) => (part ? part[0].toUpperCase() + part.slice(1) : ''))
    .join(' ')
}
